import { ErrorMessage } from "@hookform/error-message";
import { Alert, Stack, Typography } from "@mui/material";
import {
    DataGrid,
    type GridLocaleText,
    type GridRowSelectionModel,
} from "@mui/x-data-grid";
import type { FieldErrors, Noop } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FieldPaper from "../../FieldPaper/FieldPaper";
import { Loader } from "./Loader.component";
import useGetIndicators from "@/src/lib/useGetIndicators/useGetIndicators";

export interface IndicatorPropsType {
    step: number;
    selectedIndicators: string[];
    onChange: (selectedIds: string[]) => void;
    onBlur: Noop;
    errors: FieldErrors;
    version: string;
    exchangeProfile: string;
}

export const Indicators = ({
    step,
    onChange,
    selectedIndicators,
    errors,
    version,
    exchangeProfile,
}: IndicatorPropsType) => {
    const { t } = useTranslation();
    const { loading: indicatorsLoading, indicators } = useGetIndicators(
        version,
        exchangeProfile
    );

    const convertSelectionToIds = (
        selection: GridRowSelectionModel
    ): string[] => {
        if (Array.isArray(selection)) {
            return selection.map(String);
        }

        const allIndicatorPaths =
            indicators?.map((indicator) => indicator.path) || [];

        if (selection.type === "exclude") {
            const excludedIds = Array.from(selection.ids);
            return selection.ids.size === 0
                ? allIndicatorPaths
                : allIndicatorPaths.filter(
                      (path) => !excludedIds.includes(path)
                  );
        }

        return Array.from(selection.ids || []).map(String);
    };

    const columns = [
        {
            field: "name",
            headerName: t("validated-question-create.indicators.select-all"),
            flex: 1,
        },
    ];

    const gridRowSelectionModel: GridRowSelectionModel = {
        type: "include",
        ids: new Set(selectedIndicators),
    };

    const localeText: Partial<GridLocaleText> = {
        footerRowSelected(count) {
            return t("validated-question-create.indicators.rows-selected", {
                count,
            });
        },
        paginationDisplayedRows(params) {
            return t("validated-question-create.indicators.total-rows", {
                fromCount: params.from,
                visibleCount: params.to,
                totalCount: params.count,
            });
        },
    };

    if (indicatorsLoading) {
        return (
            <FieldPaper
                title={
                    <Typography variant="h6">
                        {step}.{" "}
                        {t("validated-question-create.indicators.title")}
                    </Typography>
                }
            >
                <Stack alignItems="center">
                    <Loader />
                </Stack>
            </FieldPaper>
        );
    }

    if (!indicators?.length) {
        const message = version
            ? t("validated-question-create.indicators.no-results")
            : t("validated-question-create.indicators.empty-state");

        return (
            <FieldPaper
                title={
                    <Typography variant="h6">
                        {step}.{" "}
                        {t("validated-question-create.indicators.title")}
                    </Typography>
                }
            >
                {errors.length && (
                    <Alert severity="error">
                        <ErrorMessage errors={errors} name="indicators" />
                    </Alert>
                )}
                <Alert severity={version ? "warning" : "info"}>{message}</Alert>
            </FieldPaper>
        );
    }

    return (
        <FieldPaper
            title={
                <Typography variant="h6">
                    {step}. {t("validated-question-create.indicators.title")}
                </Typography>
            }
        >
            {errors.length && (
                <Alert severity="error">
                    <ErrorMessage errors={errors} name="indicators" />
                </Alert>
            )}
            <DataGrid
                density="compact"
                rows={indicators}
                columns={columns}
                pageSizeOptions={[50]}
                getRowId={(row) => row.path}
                rowSelectionModel={gridRowSelectionModel}
                checkboxSelection
                onRowSelectionModelChange={(selection) =>
                    onChange(convertSelectionToIds(selection))
                }
                sx={{ maxHeight: 400 }}
                localeText={localeText}
            />
        </FieldPaper>
    );
};

export default Indicators;
